import { defineStore } from 'pinia';
import { getMarketCategoryPage } from '@/api/market';
import { createHomeFeedFetchOptions } from '@/domain/market/home-feed-adapter.js';
import {
  paginatedFeedOnContainerScroll,
  paginatedFeedRecoverIfStuck,
} from '@/domain/market/paginated-feed-core.js';
import { runPaginatedFeedFetchPage, runPaginatedFeedLoadMore } from '@/stores/feed-fetch-error.js';
import { shouldIgnoreFeedScrollLoad } from '@/composables/feed-viewport.js';
import {
  paginatedFeedIsManualLoadRequired,
  paginatedFeedResetAutoAppendCount,
} from '@/domain/market/paginated-feed-pagination.js';
import {
  rearrangeHomeMinuteUpdownFeedRows,
  shouldNormalizeHomeFeedCategory,
} from '@/domain/crypto/crypto-minute-feed-pagination.js';
import { getClobNowSec } from '@/domain/crypto/polymarket-clob-clock.js';

const homeFetchOptions = createHomeFeedFetchOptions();

/**
 * 首页市场 Feed 唯一状态源（H5 / PC 共用 rawRows 与 OpenAPI query）
 * - 业务状态 + fetch / pagination 在此
 * - 卡片 i18n 映射见 modules/market-feed/presentation/useMarketFeedPresentation.js
 * - 分页/loadMore 共用 domain/paginated-feed-core.js
 */
export const useMarketFeedStore = defineStore('marketFeed', {
  state: () => ({
    initialized: false,
    /** 最近一次 fetch / reload / loadMore 使用的 query 分支 */
    lastFetchPlatform: 'pc',

    categories: [],
    categoriesLoading: false,

    primaryCategory: 'all',
    sortId: 'trending',
    searchQuery: '',
    appliedQuestion: '',

    /** collection/page 原始 rows */
    rawRows: [],
    listLoading: false,
    loadingMore: false,
    hasMore: true,
    pageNum: 0,

    loadGeneration: 0,
    /** reload 时递增；composable 监听后滚顶（store 不操作 DOM） */
    reloadToken: 0,
    /** PC 自动 append 计数（H5 不使用） */
    autoAppendCount: 0,
    /** append 失败时熔断自动 loadMore */
    appendAutoLoadBlocked: false,
    /** 首屏拉取失败：network | server */
    listFetchError: null,
    clearSearchTimer: null,
  }),

  getters: {
    showListEmpty(state) {
      return !state.listLoading && !state.loadingMore && state.rawRows.length === 0;
    },
    manualLoadRequired(state) {
      return paginatedFeedIsManualLoadRequired(state);
    },
  },

  actions: {
    setLastFetchPlatform(isH5) {
      this.lastFetchPlatform = isH5 ? 'h5' : 'pc';
    },

    async ensureInit(isH5 = false) {
      this.setLastFetchPlatform(isH5);
      // 已初始化且列表「看起来够用」才跳过；全部/分钟盘 underfill（常见于旧缓存只剩 1 张）必须重拉
      const underfilledHomeAll =
        shouldNormalizeHomeFeedCategory(this.primaryCategory) &&
        !String(this.appliedQuestion ?? '').trim() &&
        this.rawRows.length > 0 &&
        this.rawRows.length < 8;
      if (this.initialized && this.rawRows.length && !underfilledHomeAll) return;
      this.recoverIfStuck();
      if (this.initialized && this.rawRows.length && !underfilledHomeAll) return;
      await Promise.all([this.loadCategories(), this.fetchPage(false)]);
    },

    recoverIfStuck() {
      paginatedFeedRecoverIfStuck(this);
    },

    normalizeCategoryRows(data) {
      if (Array.isArray(data?.rows)) return data.rows;
      if (Array.isArray(data)) return data;
      return [];
    },

    async loadCategories() {
      this.categoriesLoading = true;
      try {
        const data = await getMarketCategoryPage({ pageNum: 1, pageSize: 100 });
        this.categories = this.normalizeCategoryRows(data)
          .map((row) => ({
            slug: String(row.slug ?? row.categorySlug ?? row.category ?? '').trim(),
            name: String(row.category ?? row.categoryName ?? row.name ?? row.slug ?? '').trim(),
          }))
          .filter((row) => row.slug);
      } catch (err) {
        console.warn('[marketFeed] getMarketCategoryPage failed', err);
        this.categories = [];
      } finally {
        this.categoriesLoading = false;
      }
    },

    /** 首页刷新策略：默认「全部市场」空响应时保留已有列表（缩放/误触 reload） */
    resolveHomeRefreshRows({ apiRows, hadRows }) {
      const isDefaultFeed =
        String(this.primaryCategory ?? 'all').trim() === 'all' &&
        !String(this.appliedQuestion ?? '').trim();
      if (apiRows.length > 0 || !hadRows) return apiRows;
      if (isDefaultFeed) {
        console.warn('[marketFeed] refresh returned empty rows, keeping existing list');
        return this.rawRows;
      }
      return apiRows;
    },

    _fetchOptions(extra = {}) {
      return {
        ...homeFetchOptions,
        resolveRefreshRows: ({ apiRows, hadRows }) =>
          this.resolveHomeRefreshRows({ apiRows, hadRows }),
        ...extra,
      };
    },

    async fetchPage(append = false, options = {}) {
      await runPaginatedFeedFetchPage(this, append, this._fetchOptions(options));
      if (
        !append &&
        !options.silent &&
        this.initialized &&
        shouldNormalizeHomeFeedCategory(this.primaryCategory)
      ) {
        this.rearrangeMinuteUpdownRowsLocal();
      }
    },

    rowKey(row) {
      return String(row?.id ?? row?.collectionId ?? row?.marketId ?? '');
    },

    /**
     * @deprecated 假滚盘已禁用；跨窗请 silent refetch。保留方法以免旧调用报错。
     * @returns {boolean}
     */
    rearrangeMinuteUpdownRowsLocal() {
      if (!shouldNormalizeHomeFeedCategory(this.primaryCategory)) return false;
      if (!this.initialized || this.listLoading || this.loadingMore) return false;
      const prev = Array.isArray(this.rawRows) ? this.rawRows : [];
      const next = rearrangeHomeMinuteUpdownFeedRows(prev, this.primaryCategory, getClobNowSec());
      const unchanged =
        next.length === prev.length && next.every((row, index) => row === prev[index]);
      if (unchanged) return false;
      this.rawRows = next;
      return true;
    },

    async reload(platform) {
      if (platform === 'h5' || platform === 'pc') {
        this.lastFetchPlatform = platform;
      }
      try {
        const { trackAnalyticsLog } = await import('@/utils/analytics-track');
        trackAnalyticsLog({
          key: 'feed.refresh',
          label: 'Feed刷新',
          value: { source: 'home' },
        });
      } catch {
        /* ignore */
      }
      this.listLoading = false;
      this.loadingMore = false;
      this.pageNum = 0;
      this.hasMore = true;
      paginatedFeedResetAutoAppendCount(this);
      this.reloadToken += 1;
      await this.fetchPage(false);
    },

    async loadMore(options = {}) {
      await runPaginatedFeedLoadMore(this, this._fetchOptions(options));
    },

    selectCategory(slug) {
      const next = String(slug || 'all').trim() || 'all';
      if (this.primaryCategory === next) return;
      this.primaryCategory = next;
      this.reload();
    },

    /** 排序（OpenAPI sortType）；trending 不传 sortType */
    selectSort(nextSortId) {
      const next = String(nextSortId || 'trending').trim() || 'trending';
      if (this.sortId === next) return;
      this.sortId = next;
      this.reload();
    },

    submitSearch() {
      if (this.clearSearchTimer) {
        clearTimeout(this.clearSearchTimer);
        this.clearSearchTimer = null;
      }
      this.appliedQuestion = this.searchQuery.trim();
      this.reload();
    },

    onSearchQueryCleared() {
      if (String(this.searchQuery ?? '').trim()) return;
      if (!String(this.appliedQuestion ?? '').trim()) return;
      this.appliedQuestion = '';
      this.scheduleSearchAfterClear();
    },

    scheduleSearchAfterClear() {
      if (this.clearSearchTimer) clearTimeout(this.clearSearchTimer);
      this.clearSearchTimer = setTimeout(() => {
        this.clearSearchTimer = null;
        if (!this.searchQuery.trim()) {
          this.reload();
        }
      }, 500);
    },

    disposeSearchTimer() {
      if (this.clearSearchTimer) {
        clearTimeout(this.clearSearchTimer);
        this.clearSearchTimer = null;
      }
    },

    onContainerScroll(container) {
      paginatedFeedOnContainerScroll(this, container, {
        fetchOptions: this._fetchOptions(),
        shouldIgnoreScrollLoad: shouldIgnoreFeedScrollLoad,
      });
    },
  },
});
